import {amountHandler, noteDateHandler} from "./capsule";

// Валюта внутреннего баланса — API её не отдаёт, тикер берём из макета
export const NOTIFICATION_CURRENCY = '$TC';

// Категория нужна и для «чипа» в списке, и для фильтра на странице событий.
// Несколько типов уведомлений схлопываются в одну категорию: в макете
// все статусы вывода помечены одним чипом «Вывод»
export const NOTIFICATION_CATEGORY = {
    withdrawal: 'withdrawal',
    referralReward: 'referralReward',
    topUp: 'topUp',
    systemUpdate: 'systemUpdate',
    registration: 'registration',
    capsule: 'capsule',
    energy: 'energy',
    storage: 'storage',
    referralTransfer: 'referralTransfer',
    other: 'other'
};

const CATEGORY_BY_TYPE = {
    withdrawal_created: NOTIFICATION_CATEGORY.withdrawal,
    withdrawal_sent: NOTIFICATION_CATEGORY.withdrawal,
    withdrawal_rejected: NOTIFICATION_CATEGORY.withdrawal,
    withdrawal_failed: NOTIFICATION_CATEGORY.withdrawal,
    // вывод реферального баланса: та же категория, но суммы — вознаграждения
    referral_withdrawal_created: NOTIFICATION_CATEGORY.withdrawal,
    referral_withdrawal_sent: NOTIFICATION_CATEGORY.withdrawal,
    referral_withdrawal_rejected: NOTIFICATION_CATEGORY.withdrawal,
    referral_withdrawal_failed: NOTIFICATION_CATEGORY.withdrawal,
    referral_reward: NOTIFICATION_CATEGORY.referralReward,
    balance_topped_up: NOTIFICATION_CATEGORY.topUp,
    system_update: NOTIFICATION_CATEGORY.systemUpdate,
    referral_joined: NOTIFICATION_CATEGORY.registration,
    capsule_purchased: NOTIFICATION_CATEGORY.capsule,
    energy_purchased: NOTIFICATION_CATEGORY.energy,
    storage_purchased: NOTIFICATION_CATEGORY.storage,
    // перевод вознаграждений на основной баланс: три статуса одной заявки
    referral_transfer_created: NOTIFICATION_CATEGORY.referralTransfer,
    referral_transfer_approved: NOTIFICATION_CATEGORY.referralTransfer,
    referral_transfer_rejected: NOTIFICATION_CATEGORY.referralTransfer
};

// Подписи чипов. Ключи русские — как и везде в проекте
const CATEGORY_LABEL = {
    [NOTIFICATION_CATEGORY.withdrawal]: 'Вывод',
    [NOTIFICATION_CATEGORY.referralReward]: 'Реферальное начисление',
    [NOTIFICATION_CATEGORY.topUp]: 'Пополнение',
    [NOTIFICATION_CATEGORY.systemUpdate]: 'Обновление платформы',
    [NOTIFICATION_CATEGORY.registration]: 'Регистрация',
    [NOTIFICATION_CATEGORY.capsule]: 'Капсула',
    [NOTIFICATION_CATEGORY.energy]: 'Энергия',
    [NOTIFICATION_CATEGORY.storage]: 'Хранилище',
    [NOTIFICATION_CATEGORY.referralTransfer]: 'Перевод вознаграждений',
    [NOTIFICATION_CATEGORY.other]: 'Уведомление'
};

export const notificationCategoryHandler = (type) => (
    CATEGORY_BY_TYPE[type] || NOTIFICATION_CATEGORY.other
);

export const notificationCategoryLabel = (category, t) => (
    t(CATEGORY_LABEL[category] || CATEGORY_LABEL[NOTIFICATION_CATEGORY.other])
);

// Обратная карта: одна категория — несколько типов API
// (все статусы вывода в макете помечены одним чипом «Вывод»)
const TYPES_BY_CATEGORY = Object.keys(CATEGORY_BY_TYPE).reduce((acc, type) => {
    const category = CATEGORY_BY_TYPE[type];
    acc[category] = [...(acc[category] || []), type];
    return acc;
}, {});

// Типы, которых ещё нет в карте: бэк обещает их добавлять,
// на фронте они собираются под «Уведомление»
const unknownTypes = (availableTypes) => (
    (availableTypes || []).filter(type => !CATEGORY_BY_TYPE[type])
);

// Значение для параметра ?type= — список типов API через запятую
export const notificationTypesParam = (category, availableTypes) => {
    if (!category) {
        return '';
    }

    const types = category === NOTIFICATION_CATEGORY.other
        ? unknownTypes(availableTypes)
        : (TYPES_BY_CATEGORY[category] || []);

    if (!availableTypes || !availableTypes.length) {
        return types.join(',');
    }

    return types.filter(type => availableTypes.indexOf(type) !== -1).join(',');
};

// Фильтр «Тип события»: показываем только категории, которые сервер
// перечислил в available_types
export const notificationFilterOptions = (t, availableTypes) => {
    const categories = Object.keys(CATEGORY_LABEL).filter(category => (
        !!notificationTypesParam(category, availableTypes)
    ));

    return [
        {value: '', label: t('Все события')},
        ...categories.map(key => ({value: key, label: t(CATEGORY_LABEL[key])}))
    ];
};

// Заголовок системного обновления приходит картой переводов
const systemUpdateTitle = (titles, lang) => {
    if (!titles || typeof titles !== 'object') {
        return '';
    }
    if (titles[lang]) {
        return titles[lang];
    }
    const first = Object.keys(titles)[0];
    return first ? titles[first] : '';
};

const shortWallet = (wallet) => {
    const value = String(wallet || '');
    return value.length > 12 ? value.slice(0, 6) + '...' + value.slice(-4) : value;
};

const withCurrency = (value) => `${amountHandler(value)} ${NOTIFICATION_CURRENCY}`;

const withToken = (value, symbol) => {
    const num = Number(value);
    const amount = Number.isNaN(num) ? '0' : String(Number(num.toFixed(6)));
    return symbol ? `${amount} ${symbol}` : amount;
};

// Единая точка, где type + data превращаются в текст: API прозу не отдаёт.
// Возвращает {title, text, category, amount, amountSign, link}
export const notificationViewHandler = (item, t, lang) => {
    const type = item?.type;
    const data = item?.data || {};
    const category = notificationCategoryHandler(type);

    const base = {
        category,
        categoryLabel: notificationCategoryLabel(category, t),
        amount: '',
        amountSign: '',
        link: ''
    };

    switch (type) {
        case 'referral_joined':
            return {
                ...base,
                title: t('Новый реферал'),
                text: t('По вашей ссылке зарегистрировался новый пользователь') +
                    (data.level ? ` (${t('уровень')} ${data.level})` : '')
            };

        case 'referral_reward':
            return {
                ...base,
                title: t('Реферальное начисление'),
                text: t('Начисление за активность приглашённого пользователя') +
                    (data.level ? ` (${t('уровень')} ${data.level}${data.percent ? `, ${data.percent}%` : ''})` : ''),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'withdrawal_created':
            return {
                ...base,
                title: t('Заявка на вывод создана'),
                text: `${t('Вывод')} ${withCurrency(data.amount)} ${t('на кошелёк')} ${shortWallet(data.wallet_address)}`,
                amount: withCurrency(data.amount),
                amountSign: 'minus'
            };

        case 'withdrawal_sent':
            return {
                ...base,
                title: t('Вывод отправлен'),
                text: `${withToken(data.token_amount, data.token_symbol)} ${t('отправлены на кошелёк')} ${shortWallet(data.wallet_address)}`,
                link: data.explorer_url || ''
            };

        case 'withdrawal_rejected':
            return {
                ...base,
                title: t('Заявка на вывод отклонена'),
                text: data.comment
                    ? `${t('Средства возвращены на баланс')}. ${data.comment}`
                    : t('Средства возвращены на баланс'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'withdrawal_failed':
            return {
                ...base,
                title: t('Вывод не прошёл'),
                text: data.reason
                    ? `${t('Средства возвращены на баланс')}. ${data.reason}`
                    : t('Средства возвращены на баланс'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'referral_withdrawal_created':
            return {
                ...base,
                title: t('Заявка на вывод вознаграждений создана'),
                text: `${t('Вывод')} ${withCurrency(data.amount)} ${t('на кошелёк')} ${shortWallet(data.wallet_address)}`,
                amount: withCurrency(data.amount),
                amountSign: 'minus'
            };

        case 'referral_withdrawal_sent':
            return {
                ...base,
                title: t('Вознаграждения отправлены'),
                text: `${withToken(data.token_amount, data.token_symbol)} ${t('отправлены на кошелёк')} ${shortWallet(data.wallet_address)}`,
                link: data.explorer_url || ''
            };

        case 'referral_withdrawal_rejected':
            return {
                ...base,
                title: t('Заявка на вывод вознаграждений отклонена'),
                text: data.comment
                    ? `${t('Сумма возвращена на баланс вознаграждений')}. ${data.comment}`
                    : t('Сумма возвращена на баланс вознаграждений'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'referral_withdrawal_failed':
            return {
                ...base,
                title: t('Вывод вознаграждений не прошёл'),
                text: data.reason
                    ? `${t('Сумма возвращена на баланс вознаграждений')}. ${data.reason}`
                    : t('Сумма возвращена на баланс вознаграждений'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'balance_topped_up':
            return {
                ...base,
                title: t('Баланс пополнен'),
                text: data.token_amount
                    ? `${t('Поступление подтверждено в сети BEP-20')} — ${withToken(data.token_amount, data.token_symbol)}`
                    : t('Поступление подтверждено в сети BEP-20'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'capsule_purchased':
            return {
                ...base,
                title: t('Капсула открыта'),
                text: data.period_number
                    ? `${t('Капсула заняла место в периоде')} ${data.period_number}`
                    : t('Капсулу можно заполнять'),
                amount: withCurrency(data.amount),
                amountSign: 'minus'
            };

        case 'energy_purchased':
            return {
                ...base,
                title: t('Энергия зачислена'),
                text: data.amount
                    ? `${t('Оплачено')} ${data.amount} ${data.currency || ''}`.trim()
                    : t('Энергия зачислена на ваш аккаунт'),
                // энергия — не внутренний баланс, поэтому без валюты баланса
                amount: data.energy ? `+${data.energy}` : '',
                amountSign: 'plus'
            };

        case 'storage_purchased':
            return {
                ...base,
                title: t('Место добавлено'),
                text: `${data.gb ? `+${data.gb} GB` : t('Хранилище увеличено')}${data.ends_at ? ` · ${t('до')} ${noteDateHandler(data.ends_at)}` : ''}`,
                // платили криптой через шлюз, а не с внутреннего баланса
                amount: data.amount ? `${data.amount} ${data.currency || ''}`.trim() : '',
                amountSign: 'minus'
            };

        case 'referral_transfer_created':
            return {
                ...base,
                title: t('Заявка на перевод создана'),
                text: t('Сумма зарезервирована и ждёт подтверждения администратором'),
                amount: withCurrency(data.amount),
                amountSign: 'minus'
            };

        case 'referral_transfer_approved':
            return {
                ...base,
                title: t('Перевод подтверждён'),
                text: t('Вознаграждения зачислены на основной баланс'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        case 'referral_transfer_rejected':
            return {
                ...base,
                title: t('Заявка на перевод отклонена'),
                text: data.comment
                    ? `${t('Сумма возвращена на баланс вознаграждений')}. ${data.comment}`
                    : t('Сумма возвращена на баланс вознаграждений'),
                amount: withCurrency(data.amount),
                amountSign: 'plus'
            };

        // с API 15 текст обновления приходит в самом уведомлении на языке
        // запроса; titles оставляем как запасной вариант для старых записей
        case 'system_update':
            return {
                ...base,
                title: data.title || systemUpdateTitle(data.titles, lang) || t('Обновление платформы'),
                text: data.text || t('Теперь вам доступен новый функционал в личном кабинете')
            };

        // новые типы будут добавляться на бэке — показываем их обобщённо,
        // чтобы список не ломался
        default:
            return {
                ...base,
                title: t('Уведомление'),
                text: ''
            };
    }
};

// В макете дата разбита на число / сокращённый месяц / время
export const notificationDateHandler = (value, locale) => {
    const date = new Date(value);

    if (!value || Number.isNaN(date.getTime())) {
        return {day: '', month: '', time: ''};
    }

    let month = '';
    try {
        month = date.toLocaleString(locale || 'ru', {month: 'short'});
    } catch (e) {
        month = date.toLocaleString('ru', {month: 'short'});
    }
    month = month.replace('.', '');

    const pad = (num) => String(num).padStart(2, '0');

    return {
        day: String(date.getDate()),
        month: month.charAt(0).toUpperCase() + month.slice(1),
        time: `${pad(date.getHours())}:${pad(date.getMinutes())}`
    };
};
